import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { State } from '@app/store/state/state';
import { userHistoryRootSelector } from '@user-history/store/selectors/user-history-root.selector';
import { DetailReservation, HistoryBookingDetail } from '@user-history/entities/history-booking-detail';
import {
  UserHistoryItem, BookingPoliciesResponse, ReservationType
} from '@user-history/entities/user-history-item';
import { CancellationSuccessType } from '@user-history/entities/cancellation.response';
import * as actions from '@user-history/store/actions/user-history.actions';
import { getUserHistoryState } from '@user-history/store/selectors/ui.selector';
import { UserHistoryFormatPipe } from '@app/pipes/user-history-format.pipe';
import { UserHistoryOptions } from '@user-history/entities/user-history-options';
import { CancellationReasonSumbitVM } from '@user-history/entities/cancellation-reasons';

@Injectable()
export class UserHistoryFacade {

  constructor(
    private store: Store<State>,
    private userHistoryFormatPipe: UserHistoryFormatPipe,
  ) { }

  public cancellationSuccess$: Observable<CancellationSuccessType> = this.store.pipe(
    select(userHistoryRootSelector),
    map(state => state.cancellationSuccess)
  );

  public isLoadingCancellation$: Observable<boolean> = this.store.pipe(
    select(userHistoryRootSelector),
    map(state => state.ui.isLoadingCancellation)
  );

  public historyDetailTitle$: Observable<string> = this.store.pipe(
    select(userHistoryRootSelector),
    map(state => state.ui.historyDetailTitle)
  );

  public UiTabSelected$: Observable<UserHistoryOptions> = this.store
    .pipe(
      select(getUserHistoryState),
      map((state) => state.UiTabSelected)
    );

  public isLoadingUserHistory$: Observable<boolean> = this.store
    .pipe(
      select(getUserHistoryState),
      map((state) => state.isLoadingUserHistory)
    );

  public isLoadingDetailUserHistory$: Observable<boolean> = this.store.pipe(
    select(getUserHistoryState),
    map(state => state.isLoadingDetailHistory)
  );

  public isLoadingBookingPolicies$: Observable<boolean> = this.store.pipe(
    select(getUserHistoryState),
    map(state => state.isLoadingBookingPolicies)
  );

  public loadedList$: Observable<boolean> = this.store.pipe(
    select(getUserHistoryState),
    map(state => state.loadedList)
  );

  public historyDetailType$: Observable<ReservationType> = this.store.pipe(
    select(getUserHistoryState),
    map(state => state.historyDetailType)
  );

  public successFlightChanges$: Observable<boolean> = this.store.pipe(
    select(getUserHistoryState),
    map(state => state.successFlightChanges)
  );

  public errorFlightChanges$: Observable<boolean> = this.store.pipe(
    select(getUserHistoryState),
    map(state => state.errorFlightChanges)
  );

  public bookingPolicies$: Observable<BookingPoliciesResponse> = this.store.pipe(
    select(userHistoryRootSelector),
    map(state => state.bookingPolicies)
  );

  public userHistoryListConfirmed$: Observable<UserHistoryItem[]> = this.store.pipe(
    select(userHistoryRootSelector),
    map(state => this.userHistoryFormatPipe.transform(state.userHistoryList))
  );

  public historyBookingDetail$: Observable<HistoryBookingDetail> = this.store.pipe(
    select(userHistoryRootSelector),
    map(state => state?.historyBookingDetail)
  );

  public reservationsHistoryBookingDetail$: Observable<DetailReservation[]> = this.store.pipe(
    select(userHistoryRootSelector),
    map(state => {
      return state?.historyBookingDetail?.Reservations
    })
  );

  public resendingVoucher$: Observable<boolean> = this.store.pipe(
    select(getUserHistoryState),
    map(state => state.resendingVoucher)
  );

  public dependenciesHaveBeenLoaded$: Observable<boolean> = this.store
    .pipe(
      select(getUserHistoryState),
      map((state) => state.dependenciesHaveBeenLoaded)
    );

  public fetchUserHistoryList(userId: string): void {
    this.store.dispatch(actions.fetchUserHistoryListAction({ userId }));
  }

  public fetchHistoryBookingDetail(userId: string, detailId: string, isByValidate: boolean = false): void {
    this.store.dispatch(actions.fetchHistoryBookingDetailAction({ userId, detailId, isByValidate }));
  }

  public fetchRappiItineraryByOrderDetail(orderId: string): void {
    this.store.dispatch(actions.fetchRappiItineraryByOrderDetailAction({ orderId }));
  }

  public setHistoryDetailType(detailType: ReservationType, userId: string, detailId: string): void {
    this.store.dispatch(actions.setHistoryDetailTypeAction({ detailType, userId, detailId }));
  }

  public setHistoryDetailTitle(title: string): void {
    this.store.dispatch(actions.setHistoryDetailTitleAction({ title }));
  }

  public setTabSelected(tab: UserHistoryOptions): void {
    this.store.dispatch(actions.tabSelectedAction({ tab }));
  }

  public resetCancellationReasons(): void {
    this.store.dispatch(actions.resetCancellationReasons())
  }

  public setCancellattion(itineraryId: string, reason: CancellationReasonSumbitVM): void {
    this.store.dispatch(actions.fetchReponseCancellation({ itineraryId, reason }));
  }

  public fetchLastUserReservation(): void {
    this.store.dispatch(actions.fetchLastUserReservationAction());
  }

  public readyReservationDetail(): void {
    this.store.dispatch(actions.readyLastReservationAction());
  }

  public resetDetailReservation(): void {
    this.store.dispatch(actions.resetDetailReservationAction());
  }

  public resendVoucher(email: string, itinerary: number): void {
    this.store.dispatch(actions.resendVoucherAction({ email, itinerary }));
  }
}
